import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { api } from '../lib/api'

export function useGmail() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [newEmails, setNewEmails] = useState<number | null>(null)
  const [syncStarted, setSyncStarted] = useState(false)

  const {
    data: status,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['gmailStatus'],
    queryFn: async () => {
      const token = await getToken()
      return api.getGmailStatus(token)
    },
    refetchInterval: (query) => {
      // Poll every 2s while syncing
      if (query.state.data?.syncState?.syncing || syncStarted) return 2000
      return false
    },
  })

  const connectMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const { authUrl } = await api.startGmailAuth(token)
      window.location.href = authUrl
    },
  })

  const syncMutation = useMutation({
    mutationFn: async (range: string) => {
      setNewEmails(null)
      const token = await getToken()
      return api.syncGmail(range, token)
    },
    onSuccess: (data) => {
      if (data.newEmails) setNewEmails(data.newEmails)
      setSyncStarted(true)
      // Start polling immediately — refetchInterval handles the rest
      queryClient.invalidateQueries({ queryKey: ['gmailStatus'] })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      return api.disconnectGmail(token)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmailStatus'] })
    },
  })

  const backendSyncing = !!status?.syncState?.syncing
  const isSyncing = syncMutation.isPending || backendSyncing || syncStarted

  // Clear syncStarted once polled status confirms syncing is done
  useEffect(() => {
    if (syncStarted && !backendSyncing && !syncMutation.isPending && status) {
      setSyncStarted(false)
    }
  }, [syncStarted, backendSyncing, syncMutation.isPending, status])

  return {
    status,
    isLoading,
    error,
    connect: connectMutation.mutate,
    isConnecting: connectMutation.isPending,
    sync: syncMutation.mutateAsync,
    isSyncing,
    newEmails,
    disconnect: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
  }
}
